import { TitleTypography } from '../../components/UI/Typography';
import { IconDashboardMS } from '../../constants/icons';
import { ClientKindDonut } from './components/ClientKindDonut';
import { CriticalList } from './components/CriticalList';
import { FiltersBar } from './components/FiltersBar';
import { KpiBar } from './components/KpiBar';
import { PeriodSelector } from './components/PeriodSelector';
import { ReportFunctionBar } from './components/ReportFunctionBar';
import { StatusFunnel } from './components/StatusFunnel';
import { TopCompanies } from './components/TopCompanies';
import { TopRegions } from './components/TopRegions';
import { VolumeChart } from './components/VolumeChart';
import {
  DASHBOARD_TITLE,
  MAX_RECORDS_HINT,
  REFRESHING_LABEL,
  SECTION_TITLES,
  STATUS_HINT,
  STATUS_HINT_SUFFIX,
} from './constants';
import { useDashboardData } from './hooks/useDashboardData';
import { useDashboardFilters } from './hooks/useDashboardFilters';
import {
  ChartsGrid,
  HeaderBox,
  HeaderTopRow,
  HintBanner,
  PageContainer,
  SectionTitle,
  StaleHint,
  TitleRow,
} from './styles';

export function Dashboard(): JSX.Element {
  const {
    filters,
    setPeriod,
    setUf,
    setCompanyId,
    setClientKinds,
    setReportFunctions,
    clearAll,
  } = useDashboardFilters();
  const { data, loading, refreshing, stale } = useDashboardData(filters);

  const showHint =
    data.current.length >= MAX_RECORDS_HINT ||
    data.last12m.length >= MAX_RECORDS_HINT;

  const isRefreshing = (stale || refreshing) && !loading;

  return (
    <PageContainer>
      <HeaderBox>
        <HeaderTopRow>
          <TitleRow>
            <TitleTypography>
              {IconDashboardMS}
              {DASHBOARD_TITLE}
            </TitleTypography>
            {isRefreshing && (
              <StaleHint role="status" aria-live="polite">
                <span className="dot" aria-hidden="true" />
                {REFRESHING_LABEL}
              </StaleHint>
            )}
          </TitleRow>
          <PeriodSelector value={filters.period} onChange={setPeriod} />
        </HeaderTopRow>
        <FiltersBar
          filters={filters}
          onUf={setUf}
          onCompany={setCompanyId}
          onClientKinds={setClientKinds}
          onReportFunctions={setReportFunctions}
          onClear={clearAll}
        />
        {showHint && (
          <HintBanner>
            {STATUS_HINT} {MAX_RECORDS_HINT} {STATUS_HINT_SUFFIX}
          </HintBanner>
        )}
      </HeaderBox>

      <SectionTitle>{SECTION_TITLES.activity}</SectionTitle>
      <KpiBar
        current={data.current}
        previous={data.previous}
        loading={loading}
        period={filters.period}
      />

      <SectionTitle>{SECTION_TITLES.trendsAndState}</SectionTitle>
      <ChartsGrid>
        <VolumeChart data={data.last12m} />
        <StatusFunnel data={data.last12m} />
        <TopCompanies data={data.last12m} />
        <TopRegions data={data.last12m} />
        <ClientKindDonut data={data.last12m} />
        <ReportFunctionBar data={data.last12m} />
        <CriticalList data={data.current} />
      </ChartsGrid>
    </PageContainer>
  );
}
